import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Phone, Users, Clock, CheckCircle2, Star, Tag, Ticket, FileText } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api, fmtErr, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

function groupHours(hours = {}) {
  const groups = [];
  let start = null;
  let label = null;
  const cap = (s) => s[0].toUpperCase() + s.slice(1);
  DAYS.forEach((d, i) => {
    const h = hours[d];
    const val = h && !h.closed ? `${h.open} – ${h.close}` : "Closed";
    if (val !== label) {
      if (start !== null) groups.push({ days: start === i - 1 ? cap(DAYS[start]) : `${cap(DAYS[start])} – ${cap(DAYS[i - 1])}`, hours: label });
      start = i;
      label = val;
    }
  });
  if (start !== null) groups.push({ days: start === DAYS.length - 1 ? cap(DAYS[start]) : `${cap(DAYS[start])} – ${cap(DAYS[DAYS.length - 1])}`, hours: label });
  return groups;
}

export default function RestaurantDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [restaurant, setRestaurant] = useState(null);
  const [error, setError] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [party, setParty] = useState("2");
  const [slots, setSlots] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "", occasion: "", dietary: "" });
  const [answers, setAnswers] = useState({});

  useEffect(() => {
    if (!restaurant) return;
    const prev = document.title;
    document.title = `${restaurant.name} — TableDiary`;
    const setOg = (prop, content) => {
      let tag = document.querySelector(`meta[property="${prop}"]`);
      if (!tag) { tag = document.createElement("meta"); tag.setAttribute("property", prop); document.head.appendChild(tag); }
      tag.content = content;
    };
    setOg("og:title", `${restaurant.name} — book a table`);
    setOg("og:description", [restaurant.cuisine, restaurant.city].filter(Boolean).join(" · ") || "Book a table");
    const img = restaurant.header_image_url || restaurant.logo_url || restaurant.image_url;
    if (img) setOg("og:image", img.startsWith("/") ? `${API}${img}` : img);
    return () => { document.title = prev; };
  }, [restaurant]);
  const [events, setEvents] = useState([]);
  const [ticketForms, setTicketForms] = useState({});
  const [confirmed, setConfirmed] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [offers, setOffers] = useState([]);
  const [reviewForm, setReviewForm] = useState({ rating: 0, comment: "" });
  const [menus, setMenus] = useState([]);
  const [seasonalMenus, setSeasonalMenus] = useState([]);
  const [setMenuId, setSetMenuId] = useState("");
  const [channelSrc] = useState(() => new URLSearchParams(window.location.search).get("src") || "");
  const [voucherOpen, setVoucherOpen] = useState(false);
  const [vform, setVform] = useState({ buyer_name: "", buyer_email: "", recipient_name: "", amount: "", message: "" });
  const [vbuying, setVbuying] = useState(false);
  const [voucherCode, setVoucherCode] = useState("");
  const [wlForm, setWlForm] = useState({ name: "", email: "" });
  const [wlDone, setWlDone] = useState(false);

  const loadReviews = () => restaurant && api.get(`/restaurants/${restaurant.id}/reviews`).then((r) => setReviews(r.data)).catch(() => {});

  useEffect(() => {
    api.get(`/restaurants/${id}`)
      .then((r) => setRestaurant(r.data))
      .catch(() => setError("Restaurant not found"));
  }, [id]);

  useEffect(() => {
    if (!restaurant) return;
    loadReviews();
    api.get(`/restaurants/${restaurant.id}/offers`).then((r) => setOffers(r.data)).catch(() => {});
    api.get(`/restaurants/${restaurant.id}/menus`).then((r) => setMenus(r.data)).catch(() => {});
    api.get(`/restaurants/${restaurant.id}/set-menus`).then((r) => setSeasonalMenus(r.data)).catch(() => {});
    api.get(`/restaurants/${restaurant.id}/events`).then((r) => setEvents(r.data)).catch(() => {});
  }, [restaurant?.id]);

  const buyTicket = async (ev) => {
    const tf = ticketForms[ev.id] || {};
    const name = tf.name || form.name;
    const email = tf.email || form.email;
    const qty = tf.qty || 1;
    if (!name || !email) { toast.error("Enter your name and email"); return; }
    try {
      const { data } = await api.post(`/events/${ev.id}/tickets`, { name, email, qty });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const joinWaitlist = async () => {
    try {
      await api.post(`/restaurants/${restaurant.id}/waitlist`, {
        name: wlForm.name, email: wlForm.email, date, party_size: Number(party),
      });
      setWlDone(true);
      toast.success("You're on the waitlist — we'll email you if a table opens");
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const submitReview = async () => {
    try {
      const { data } = await api.post(`/restaurants/${restaurant.id}/reviews`, reviewForm);
      toast.success("Review posted");
      setReviewForm({ rating: 0, comment: "" });
      loadReviews();
      setRestaurant((r) => ({ ...r, avg_rating: data.avg_rating, review_count: data.review_count }));
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: f.name || user.name, email: f.email || user.email }));
  }, [user]);

  const checkAvailability = async () => {
    setSelectedSlot(null);
    try {
      const { data } = await api.get(`/restaurants/${restaurant.id}/availability`, { params: { date, party_size: Number(party) } });
      setSlots(data.slots);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const book = async () => {
    const wf = restaurant.widget_form || {};
    if (!form.phone.trim()) { toast.error("Phone number is required"); return; }
    if (wf.require_dietary && !form.dietary.trim()) { toast.error("Dietary / allergy info is required by this restaurant"); return; }
    if (wf.require_notes && !form.notes.trim()) { toast.error("A note is required by this restaurant"); return; }
    for (const q of (restaurant.custom_questions || [])) {
      if (q.required && !(answers[q.id] || "").trim()) { toast.error(`Please answer: ${q.label}`); return; }
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/restaurants/${restaurant.id}/bookings`, {
        ...form, date, time: selectedSlot, party_size: Number(party), source: channelSrc || "web",
        set_menu_id: setMenuId || null,
        voucher_code: voucherCode.trim() || null,
        answers: Object.entries(answers).map(([id, answer]) => ({ id, answer })),
      });
      if (data.checkout_url) {
        toast.info("Redirecting to secure deposit payment...");
        window.location.href = data.checkout_url;
        return;
      }
      setConfirmed(data);
      toast.success("Booking confirmed — check your email");
    } catch (e) {
      toast.error(fmtErr(e));
    }
    setSubmitting(false);
  };

  if (error) return <div data-testid="restaurant-error" className="max-w-3xl mx-auto px-6 py-32 text-center text-muted-foreground">{error}</div>;
  if (!restaurant) return <div className="min-h-screen bg-background" />;

  const maxParty = restaurant.booking_policy?.max_party_size || 12;
  const wf = restaurant.widget_form || {};
  const missingWf = (!form.phone.trim())
    || (wf.require_dietary && !form.dietary.trim())
    || (wf.require_notes && !form.notes.trim());

  return (
    <div data-testid="restaurant-detail-page" className="bg-background min-h-screen">
      <section className="dark relative h-[46vh] overflow-hidden">
        <img src={(restaurant.header_image_url || restaurant.image_url) || "https://images.pexels.com/photos/29962487/pexels-photo-29962487.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"}
          alt={restaurant.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-black/40 to-transparent" />
        <div className="absolute bottom-0 max-w-7xl mx-auto px-6 pb-10 w-full left-0 right-0">
          <p className="text-xs tracking-[0.3em] uppercase text-primary">{restaurant.cuisine}</p>
          <div className="flex items-center gap-4 mt-2">
            {restaurant.logo_url && (
              <img src={restaurant.logo_url.startsWith("/") ? `${API}${restaurant.logo_url}` : restaurant.logo_url}
                alt={`${restaurant.name} logo`} data-testid="restaurant-logo"
                className="w-14 h-14 rounded-xl border border-border/60 object-cover shrink-0" />
            )}
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight text-foreground">{restaurant.name}</h1>
          </div>
          <div className="flex flex-wrap gap-5 mt-3 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {restaurant.address}{restaurant.city ? `, ${restaurant.city}` : ""}</span>
            {restaurant.phone && <span className="flex items-center gap-1.5"><Phone className="w-4 h-4" /> {restaurant.phone}</span>}
            {restaurant.review_count > 0 && (
              <span data-testid="restaurant-rating" className="flex items-center gap-1.5 text-amber-300">
                <Star className="w-4 h-4 fill-amber-300" /> {restaurant.avg_rating} · {restaurant.review_count} review{restaurant.review_count === 1 ? "" : "s"}
              </span>
            )}
            {restaurant.voucher_url ? (
              <a data-testid="gift-vouchers-link" href={restaurant.voucher_url} target="_blank" rel="noreferrer"
                onClick={() => { try { navigator.sendBeacon(`${API}/api/restaurants/${restaurant.id}/voucher-click`); } catch (e) { /* ignore */ } }}
                className="flex items-center gap-1.5 text-primary hover:underline">
                Gift vouchers
              </a>
            ) : restaurant.stripe_connected ? (
              <button data-testid="gift-vouchers-link" onClick={() => setVoucherOpen((v) => !v)}
                className="flex items-center gap-1.5 text-primary hover:underline">
                Gift vouchers
              </button>
            ) : (
              <a data-testid="gift-vouchers-link" href="https://giftvouchers.app" target="_blank" rel="noreferrer"
                onClick={() => { try { navigator.sendBeacon(`${API}/api/restaurants/${restaurant.id}/voucher-click`); } catch (e) { /* ignore */ } }}
                className="flex items-center gap-1.5 text-primary hover:underline">
                Gift vouchers
              </a>
            )}
            {voucherOpen && restaurant.stripe_connected && !restaurant.voucher_url && (
              <div data-testid="voucher-buy-form" className="basis-full w-full mt-4 rounded-2xl border border-border/60 bg-card p-5 space-y-3 max-w-md text-left">
                <p className="text-sm font-medium">Buy a gift voucher</p>
                <Input data-testid="voucher-recipient-input" placeholder="Recipient's name" value={vform.recipient_name}
                  onChange={(e) => setVform({ ...vform, recipient_name: e.target.value })} className="bg-background border-border/60" />
                <Input data-testid="voucher-buyer-name-input" placeholder="Your name" value={vform.buyer_name}
                  onChange={(e) => setVform({ ...vform, buyer_name: e.target.value })} className="bg-background border-border/60" />
                <Input data-testid="voucher-buyer-email-input" type="email" placeholder="Your email (code is sent here)" value={vform.buyer_email}
                  onChange={(e) => setVform({ ...vform, buyer_email: e.target.value })} className="bg-background border-border/60" />
                <Input data-testid="voucher-amount-input" type="number" min="5" max="2000" step="5" placeholder="Amount (£)" value={vform.amount}
                  onChange={(e) => setVform({ ...vform, amount: e.target.value })} className="bg-background border-border/60" />
                <Textarea data-testid="voucher-message-input" rows={2} placeholder="Add a message (optional)" value={vform.message}
                  onChange={(e) => setVform({ ...vform, message: e.target.value })} className="bg-background border-border/60" />
                <Button data-testid="voucher-buy-btn" className="w-full rounded-full" disabled={vbuying}
                  onClick={async () => {
                    setVbuying(true);
                    try {
                      const { data } = await api.post(`/restaurants/${restaurant.id}/vouchers`, { ...vform, amount: Number(vform.amount) });
                      window.location.href = data.checkout_url;
                    } catch (e) {
                      toast.error(fmtErr(e));
                      setVbuying(false);
                    }
                  }}>
                  {vbuying ? "Redirecting to payment..." : "Buy voucher"}
                </Button>
              </div>
            )}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 py-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8">
          <h2 className="font-display text-2xl tracking-tight">About</h2>
          <p className="mt-4 text-muted-foreground leading-relaxed">{restaurant.description || "A wonderful place to dine."}</p>

          {menus.length > 0 && (
            <div className="mt-12" data-testid="menus-section">
              <h2 className="font-display text-2xl tracking-tight">Menus</h2>
              <div className="mt-5 space-y-6">
                {menus.map((m) => (
                  <div key={m.id} data-testid={`menu-${m.id}`} className="rounded-2xl border border-border/60 bg-card p-6">
                    <h3 className="font-outfit font-medium text-lg">{m.name}</h3>
                    {m.file && (
                      <a data-testid={`menu-file-${m.id}`} href={`${API}/menu-files/${m.file.path}`} target="_blank" rel="noreferrer"
                        className="mt-2 inline-flex items-center gap-1.5 text-sm text-primary hover:underline">
                        <FileText className="w-4 h-4" /> View menu file ({m.file.filename})
                      </a>
                    )}
                    {m.description && <p className="text-sm text-muted-foreground mt-1">{m.description}</p>}
                    <div className="mt-4 space-y-5">
                      {m.sections.map((s, si) => (
                        <div key={si}>
                          <p className="text-xs tracking-[0.2em] uppercase text-primary mb-2.5">{s.title}</p>
                          <div className="space-y-2.5">
                            {s.items.map((it, ii) => (
                              <div key={ii} className="flex items-baseline justify-between gap-4">
                                <div>
                                  <p className="text-sm font-medium">{it.name}</p>
                                  {it.description && <p className="text-xs text-muted-foreground mt-0.5">{it.description}</p>}
                                </div>
                                {it.price && <span className="text-sm font-outfit shrink-0">{it.price}</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {seasonalMenus.length > 0 && (
            <div className="mt-12" data-testid="set-menus-public">
              <h2 className="font-display text-2xl tracking-tight">Seasonal set menus</h2>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {seasonalMenus.map((sm) => (
                  <div key={sm.id} data-testid={`set-menu-public-${sm.id}`} className="rounded-2xl border border-border/60 bg-card p-6">
                    <div className="flex items-baseline justify-between gap-3">
                      <h3 className="font-outfit font-medium text-lg">{sm.name}</h3>
                      <p className="font-outfit text-primary shrink-0">£{Number(sm.price).toFixed(2)} pp</p>
                    </div>
                    {sm.description && <p className="text-sm text-muted-foreground mt-1.5">{sm.description}</p>}
                    {(sm.courses || []).length > 0 && (
                      <ul className="mt-3 space-y-1">
                        {sm.courses.map((c, i) => (
                          <li key={i} className="text-sm text-muted-foreground flex gap-2"><span className="text-primary">·</span>{c}</li>
                        ))}
                      </ul>
                    )}
                    <p className="text-xs text-muted-foreground mt-3">
                      {sm.valid_from || sm.valid_to ? `Available ${sm.valid_from || "now"} → ${sm.valid_to || "ongoing"} · ` : ""}
                      Min party {sm.min_party}{sm.prepay ? " · prepayment required" : ""} — add it when you book.
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
          <h2 className="font-display text-2xl tracking-tight mt-12 flex items-center gap-2"><Clock className="w-5 h-5 text-primary" /> Hours</h2>
          <div className="mt-3 flex flex-wrap gap-x-8 gap-y-1.5 text-sm text-muted-foreground">
            {groupHours(restaurant.operating_hours).map((g) => (
              <span key={g.days}><span className="text-foreground font-medium">{g.days}</span> · {g.hours}</span>
            ))}
          </div>
          {(restaurant.photos || []).length > 0 && (
            <div className="mt-12" data-testid="photo-gallery">
              <h2 className="font-display text-2xl tracking-tight">Gallery</h2>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
                {restaurant.photos.map((p, i) => (
                  <img key={i} src={p} alt={`${restaurant.name} photo ${i + 1}`}
                    className="rounded-xl border border-border/60 h-36 w-full object-cover hover:opacity-90 transition-opacity duration-200" />
                ))}
              </div>
            </div>
          )}

          {offers.length > 0 && (
            <div className="mt-12" data-testid="offers-list">
              <h2 className="font-display text-2xl tracking-tight flex items-center gap-2">
                <Tag className="w-5 h-5 text-primary" /> Offers
              </h2>
              <div className="mt-4 space-y-3">
                {offers.map((o) => (
                  <div key={o.id} data-testid={`offer-${o.id}`} className="rounded-xl border border-primary/30 bg-primary/5 p-5">
                    <h3 className="font-outfit font-medium">{o.title}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{o.description}</p>
                    {o.valid_until && <p className="text-xs text-muted-foreground mt-2">Valid until {o.valid_until}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {events.length > 0 && (
            <div className="mt-12" data-testid="events-section">
              <h2 className="font-display text-2xl tracking-tight flex items-center gap-2">
                <Ticket className="w-5 h-5 text-primary" /> Upcoming events
              </h2>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {events.map((ev) => {
                  const tf = ticketForms[ev.id] || { name: "", email: "", qty: 1 };
                  const setTf = (patch) => setTicketForms({ ...ticketForms, [ev.id]: { ...tf, ...patch } });
                  const left = ev.capacity - ev.sold;
                  return (
                    <div key={ev.id} data-testid={`event-${ev.id}`} className="rounded-xl border border-border/60 bg-card p-5">
                      {ev.image_url && <img src={ev.image_url} alt={ev.title} className="rounded-lg h-32 w-full object-cover mb-4" />}
                      <h3 className="font-outfit font-medium">{ev.title}</h3>
                      <p className="text-xs text-muted-foreground mt-1">{ev.date} · {ev.time}</p>
                      {ev.description && <p className="text-sm text-muted-foreground mt-2">{ev.description}</p>}
                      <div className="flex items-center justify-between mt-3">
                        <p className="text-sm font-medium">{ev.price > 0 ? `£${Number(ev.price).toFixed(2)} / ticket` : "Free entry"}</p>
                        <p className="text-xs text-muted-foreground">{left > 0 ? `${left} left` : "Sold out"}</p>
                      </div>
                      {left > 0 && ev.price > 0 && (
                        <div className="mt-4 space-y-2.5">
                          <div className="grid grid-cols-2 gap-2">
                            <Input data-testid={`ticket-name-${ev.id}`} placeholder="Name" value={tf.name}
                              onChange={(e) => setTf({ name: e.target.value })} className="bg-background border-border/60 h-9 text-sm" />
                            <Input data-testid={`ticket-email-${ev.id}`} type="email" placeholder="Email" value={tf.email}
                              onChange={(e) => setTf({ email: e.target.value })} className="bg-background border-border/60 h-9 text-sm" />
                          </div>
                          <div className="flex gap-2">
                            <Select value={String(tf.qty)} onValueChange={(v) => setTf({ qty: Number(v) })}>
                              <SelectTrigger data-testid={`ticket-qty-${ev.id}`} className="bg-background border-border/60 h-9 text-sm w-24">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {[1, 2, 3, 4, 5, 6].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                              </SelectContent>
                            </Select>
                            <Button data-testid={`buy-ticket-${ev.id}`} size="sm" onClick={() => buyTicket(ev)} className="rounded-full flex-1">
                              Buy tickets
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mt-12" data-testid="reviews-section">
            <h2 className="font-display text-2xl tracking-tight">Reviews</h2>
            <div className="mt-4 space-y-4">
              {reviews.map((rv, i) => (
                <div key={i} data-testid={`review-${i}`} className="rounded-xl border border-border/60 bg-card p-5">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-sm">{rv.name}</p>
                    <div className="flex gap-0.5">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star key={n} className={`w-3.5 h-3.5 ${n <= rv.rating ? "text-amber-300 fill-amber-300" : "text-muted-foreground"}`} />
                      ))}
                    </div>
                  </div>
                  {rv.comment && <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{rv.comment}</p>}
                  {rv.reply && (
                    <div data-testid={`review-reply-${i}`} className="mt-3 rounded-lg bg-primary/5 border-l-2 border-primary px-3 py-2">
                      <p className="text-[10px] tracking-[0.15em] uppercase text-primary">Response from {restaurant.name}</p>
                      <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{rv.reply}</p>
                    </div>
                  )}
                </div>
              ))}
              {reviews.length === 0 && <p className="text-sm text-muted-foreground">No reviews yet — be the first after your visit.</p>}
            </div>
            {user?.role === "diner" && (
              <div className="mt-6 rounded-xl border border-border/60 bg-card p-5" data-testid="review-form">
                <p className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Leave a review</p>
                <div className="flex gap-1 mt-3">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} data-testid={`review-star-${n}`} onClick={() => setReviewForm({ ...reviewForm, rating: n })}
                      className={`w-6 h-6 cursor-pointer transition-colors ${n <= reviewForm.rating ? "text-amber-300 fill-amber-300" : "text-muted-foreground hover:text-amber-200"}`} />
                  ))}
                </div>
                <Textarea data-testid="review-comment-input" rows={2} placeholder="How was your visit?" value={reviewForm.comment}
                  onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                  className="mt-3 bg-background border-border/60" />
                <Button data-testid="review-submit-btn" onClick={submitReview} disabled={!reviewForm.rating}
                  className="mt-3 rounded-full" size="sm">
                  Post review
                </Button>
              </div>
            )}
          </div>

          <p className="mt-8 text-xs text-muted-foreground">
            Free cancellation up to {restaurant.booking_policy?.cancellation_hours ?? 24}h before your reservation.
          </p>
        </div>

        <div className="lg:col-span-4">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="rounded-2xl border border-border bg-card p-7 shadow-2xl sticky top-24">
            {confirmed ? (
              <div data-testid="booking-confirmation" className="text-center py-6">
                <CheckCircle2 className="w-12 h-12 text-primary mx-auto" />
                <h3 className="font-display text-2xl mt-4">You're booked</h3>
                <p className="text-muted-foreground mt-3 text-sm leading-relaxed">
                  {restaurant.name} · {confirmed.date} at {confirmed.time}<br />
                  Party of {confirmed.party_size} · Table {confirmed.table_label}<br />
                  Ref: <span className="text-foreground">{confirmed.id.slice(0, 8)}</span>
                </p>
                <p className="text-xs text-muted-foreground mt-4">A confirmation email is on its way to {confirmed.email}.</p>
                <Link to={`/find-booking?ref=${confirmed.id.slice(0, 8)}&email=${encodeURIComponent(confirmed.email)}`}
                  data-testid="manage-booking-link" className="inline-block mt-3 text-sm text-primary hover:underline">
                  Manage or cancel this booking
                </Link>
                <div className="mt-6 flex gap-3 justify-center">
                  {user?.role === "diner" && (
                    <Link to="/my-bookings"><Button data-testid="view-my-bookings-btn" variant="outline" className="rounded-full">My bookings</Button></Link>
                  )}
                  <Button data-testid="book-another-btn" variant="ghost" className="rounded-full"
                    onClick={() => { setConfirmed(null); setSlots(null); setSelectedSlot(null); }}>
                    Book another
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <h3 className="font-outfit font-medium text-xl">Reserve a table</h3>
                {restaurant.online_ordering?.enabled && (
                  <Link to={`/order/${restaurant.slug || restaurant.id}`} data-testid="order-online-link"
                    className="mt-3 flex items-center justify-center gap-2 rounded-full border border-primary/50 bg-primary/5 px-4 py-2.5 text-sm text-primary hover:bg-primary/10 transition-colors">
                    Order collection / delivery
                  </Link>
                )}
                <div className="grid grid-cols-2 gap-3 mt-5">
                  <div>
                    <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Date</Label>
                    <Input data-testid="booking-date-input" type="date" value={date} min={new Date().toISOString().slice(0, 10)}
                      onChange={(e) => { setDate(e.target.value); setSlots(null); setSelectedSlot(null); }}
                      className="mt-1.5 bg-background border-border/60" />
                  </div>
                  <div>
                    <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Party</Label>
                    <Select value={party} onValueChange={(v) => { setParty(v); setSlots(null); setSelectedSlot(null); }}>
                      <SelectTrigger data-testid="booking-party-select" className="mt-1.5 bg-background border-border/60">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: maxParty }, (_, i) => (
                          <SelectItem key={i + 1} value={String(i + 1)}>{i + 1} {i === 0 ? "guest" : "guests"}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button data-testid="check-availability-btn" onClick={checkAvailability} className="w-full mt-4 rounded-full" variant="secondary">
                  <Users className="w-4 h-4 mr-2" /> Check availability
                </Button>

                {slots && (
                  <div data-testid="slots-grid" className="mt-5">
                    {slots.length === 0 ? (
                      <div data-testid="waitlist-panel" className="rounded-xl border border-primary/30 bg-primary/5 p-5">
                        {wlDone ? (
                          <p className="text-sm text-center py-2 text-emerald-500">You're on the waitlist for {date}.</p>
                        ) : (
                          <>
                            <p className="text-sm font-medium">Fully booked for this date</p>
                            <p className="text-xs text-muted-foreground mt-1 mb-3">Join the waitlist — we'll email you the moment a table opens.</p>
                            <div className="space-y-2.5">
                              <Input data-testid="waitlist-name-input" placeholder="Name" value={wlForm.name}
                                onChange={(e) => setWlForm({ ...wlForm, name: e.target.value })} className="bg-background border-border/60 h-9 text-sm" />
                              <Input data-testid="waitlist-email-input" type="email" placeholder="Email" value={wlForm.email}
                                onChange={(e) => setWlForm({ ...wlForm, email: e.target.value })} className="bg-background border-border/60 h-9 text-sm" />
                              <Button data-testid="waitlist-join-btn" size="sm" onClick={joinWaitlist}
                                disabled={!wlForm.name || !wlForm.email} className="w-full rounded-full">
                                Join waitlist
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="grid grid-cols-4 gap-2 max-h-44 overflow-y-auto pr-1">
                        {slots.map((s) => (
                          <button key={s.time} data-testid={`slot-${s.time}`} onClick={() => setSelectedSlot(s.time)}
                            className={`rounded-lg border px-2 py-2 text-sm transition-colors duration-200 ${
                              selectedSlot === s.time
                                ? "bg-primary text-primary-foreground border-primary"
                                : "border-border/60 hover:border-primary/60 hover:bg-accent"
                            }`}>
                            {s.time}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {selectedSlot && (
                  <div className="mt-5 space-y-3">
                    <Input data-testid="booking-name-input" placeholder="Full name" value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-background border-border/60" />
                    <Input data-testid="booking-email-input" type="email" placeholder="Email" value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-background border-border/60" />
                    <Input data-testid="booking-phone-input" placeholder="Phone *" value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-background border-border/60" />
                    <Input data-testid="booking-dietary-input" placeholder={`Dietary / allergies${wf.require_dietary ? " *" : " (optional)"}`} value={form.dietary}
                      onChange={(e) => setForm({ ...form, dietary: e.target.value })} className="bg-background border-border/60" />
                    {wf.show_occasion !== false && (
                      <Select value={form.occasion} onValueChange={(v) => setForm({ ...form, occasion: v === "none" ? "" : v })}>
                        <SelectTrigger data-testid="booking-occasion-select" className="bg-background border-border/60">
                          <SelectValue placeholder="Occasion (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No occasion</SelectItem>
                          {["Birthday", "Anniversary", "Date night", "Business meal", "Celebration", "Other"].map((o) => (
                            <SelectItem key={o} value={o}>{o}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {seasonalMenus.length > 0 && (
                      <Select value={setMenuId} onValueChange={(v) => setSetMenuId(v === "none" ? "" : v)}>
                        <SelectTrigger data-testid="booking-set-menu-select" className="bg-background border-border/60">
                          <SelectValue placeholder="Add a set menu (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No set menu</SelectItem>
                          {seasonalMenus.map((sm) => (
                            <SelectItem key={sm.id} value={sm.id}>
                              {sm.name} — £{Number(sm.price).toFixed(2)} pp{sm.prepay ? " (prepaid)" : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <Input data-testid="booking-voucher-input" placeholder="Gift voucher code (optional)" value={voucherCode}
                      onChange={(e) => setVoucherCode(e.target.value.toUpperCase())} className="bg-background border-border/60" />
                    <Textarea data-testid="booking-notes-input" placeholder={`Special requests${wf.require_notes ? " *" : " (optional)"}`} value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-background border-border/60" rows={2} />
                    {(restaurant.custom_questions || []).map((q) => (
                      <div key={q.id}>
                        {q.type === "yesno" || q.type === "choice" ? (
                          <Select value={answers[q.id] || ""} onValueChange={(v) => setAnswers({ ...answers, [q.id]: v })}>
                            <SelectTrigger data-testid={`booking-q-${q.id}`} className="bg-background border-border/60">
                              <SelectValue placeholder={`${q.label}${q.required ? " *" : ""}`} />
                            </SelectTrigger>
                            <SelectContent>
                              {(q.type === "yesno" ? ["Yes", "No"] : q.options).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input data-testid={`booking-q-${q.id}`} placeholder={`${q.label}${q.required ? " *" : " (optional)"}`}
                            value={answers[q.id] || ""} onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                            className="bg-background border-border/60" />
                        )}
                      </div>
                    ))}
                    {missingWf && (
                      <p className="text-xs text-amber-500" data-testid="booking-required-hint">Fields marked * are required by this restaurant.</p>
                    )}
                    <Button data-testid="confirm-booking-btn" onClick={book}
                      disabled={submitting || !form.name || !form.email || missingWf}
                      className="w-full rounded-full">
                      {submitting ? "Booking..." : `Confirm ${selectedSlot} · party of ${party}`}
                    </Button>
                  </div>
                )}
              </>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
